import { useState, useEffect, useRef } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Alert,
  Paper,
  Stack,
  Skeleton,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ChecklistRtl as ChecklistIcon,
} from '@mui/icons-material'
import { outreachChecklistsApi, OutreachChecklist } from '../services/api'
import { useNotification } from '../hooks/useNotification'
import PageHeader from '../components/common/PageHeader'
import EmptyState from '../components/common/EmptyState'
import ResponsiveSheet from '../components/common/ResponsiveSheet'
import TableSkeleton from '../components/common/table/TableSkeleton'
import PageHelpButton from '../components/PageHelpButton'
import { getEnhancedTableStyles } from '../utils/tableStyles'
import { OUTREACH_STAGE_OPTIONS } from '../constants/networking'

type ItemRow = { id: string; text: string }

const newItemId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `item-${Date.now()}-${Math.random().toString(36).slice(2)}`

function SortableItemRow({
  item,
  isDirty,
  onChange,
  onDelete,
  inputRef,
}: {
  item: ItemRow
  isDirty: boolean
  onChange: (text: string) => void
  onDelete: () => void
  inputRef?: (el: HTMLInputElement | null) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pr: 1,
        py: 0.5,
        pl: isDirty ? '5px' : 1,
        borderLeft: isDirty ? '3px solid' : '3px solid transparent',
        borderLeftColor: isDirty ? 'warning.main' : 'transparent',
        borderRadius: 1,
        // Palette-backed rather than a literal orange. The previous value was
        // MUI's default amber, which is not in this theme, so an unsaved row
        // was tinted a colour that appears nowhere else in the app.
        bgcolor: (t) => (isDirty ? alpha(t.palette.warning.main, 0.1) : 'transparent'),
        transition: 'background-color 150ms ease, border-color 150ms ease',
      }}
    >
      <IconButton
        size="small"
        aria-label="Drag to reorder"
        // 44px rather than MUI's 30px small default. This handle is the only way
        // to reorder on touch, and `touchAction: 'none'` means a thumb that misses
        // it neither drags nor scrolls.
        sx={{ cursor: 'grab', touchAction: 'none', width: 44, height: 44 }}
        {...attributes}
        {...listeners}
      >
        <DragIcon fontSize="small" color="action" />
      </IconButton>
      <TextField
        value={item.text}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
        placeholder="Checklist item"
        inputRef={inputRef}
      />
      <IconButton size="small" aria-label="Delete item" onClick={onDelete} sx={{ width: 44, height: 44 }}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

function ChecklistClientPreview({ title, items }: { title: string; items: string[] }) {
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const visibleItems = items.map((t) => t.trim()).filter((t) => t.length > 0)
  const completedCount = visibleItems.filter((item) => !!checked[item]).length
  const totalCount = visibleItems.length

  const displayTitle = title.trim().length > 0 ? title : 'Checklist title'

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'background.default' }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}
      >
        Client view preview — interactions here are not saved
      </Typography>
      {totalCount === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Add at least one item to see the client preview.
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">{displayTitle}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {completedCount}/{totalCount}
            </Typography>
          </Box>
          {/* Mirrors OutreachChecklist exactly — progress bar and 44px rows
              included. This is sold to the admin as "client view preview", so
              any difference between the two is the preview telling a lie. */}
          <Box sx={{ height: 4, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden', mb: 1 }}>
            <Box
              sx={{
                height: 4,
                borderRadius: 99,
                bgcolor: 'primary.main',
                width: `${totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0}%`,
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {visibleItems.map((item, index) => (
              <FormControlLabel
                key={`${index}-${item}`}
                control={
                  <Checkbox
                    size="small"
                    checked={!!checked[item]}
                    onChange={() => setChecked((prev) => ({ ...prev, [item]: !prev[item] }))}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      textDecoration: checked[item] ? 'line-through' : 'none',
                      color: checked[item] ? 'text.secondary' : 'text.primary',
                    }}
                  >
                    {item}
                  </Typography>
                }
                sx={{ ml: 0, mr: 0, minHeight: 44, alignItems: 'center' }}
              />
            ))}
          </Box>
        </Paper>
      )}
    </Paper>
  )
}

// Checklists are keyed by outreach stage: the three contact statuses plus the
// pre-outreach stage an unactioned contact sits in.
const STAGES = OUTREACH_STAGE_OPTIONS.map(({ value, label }) => ({ value, label }))

const stageLabel = (stage: string) => STAGES.find((s) => s.value === stage)?.label || stage

export default function ManageChecklists() {
  const theme = useTheme()
  const tableStyles = getEnhancedTableStyles(theme)
  // 768 is the width DESIGN.md names, written literally rather than borrowed
  // from an MUI breakpoint that happens to sit nearby.
  const isPhone = useMediaQuery(theme.breakpoints.down(768))
  const [checklists, setChecklists] = useState<OutreachChecklist[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [editingChecklist, setEditingChecklist] = useState<OutreachChecklist | null>(null)
  const [deletingId, setDeletingId] = useState('')
  const { showSuccess, showError } = useNotification()

  // Form state
  const [formStage, setFormStage] = useState('not_contacted')
  const [formTitle, setFormTitle] = useState('')
  const [formItems, setFormItems] = useState<ItemRow[]>([])
  const [originalTitle, setOriginalTitle] = useState('')
  const [originalItemsSnapshot, setOriginalItemsSnapshot] = useState<Record<string, string>>({})
  const [formIsDefault, setFormIsDefault] = useState(true)
  const [formClientId, setFormClientId] = useState('')
  const [saving, setSaving] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const focusItemIdRef = useRef<string | null>(null)
  const itemInputsRef = useRef<Record<string, HTMLInputElement | null>>({})

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  useEffect(() => {
    if (focusItemIdRef.current) {
      const el = itemInputsRef.current[focusItemIdRef.current]
      if (el) {
        el.focus()
        focusItemIdRef.current = null
      }
    }
  }, [formItems])

  const fetchChecklists = async () => {
    setLoading(true)
    try {
      const result = await outreachChecklistsApi.manage({ action: 'list' })
      setChecklists(Array.isArray(result) ? result : [])
    } catch (err) {
      console.error('Failed to load checklists:', err)
      setError('Failed to load checklists')
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchChecklists()
  }, [])

  const openCreateDialog = () => {
    setEditingChecklist(null)
    setFormStage('not_contacted')
    setFormTitle('')
    setOriginalTitle('')
    setFormItems([])
    setOriginalItemsSnapshot({})
    setFormIsDefault(true)
    setFormClientId('')
    setPreviewOpen(false)
    setDialogOpen(true)
  }

  const openEditDialog = (checklist: OutreachChecklist) => {
    const rows: ItemRow[] = checklist.items
      .filter((t) => t && t.trim().length > 0)
      .map((text) => ({ id: newItemId(), text }))
    const snapshot: Record<string, string> = {}
    rows.forEach((r) => { snapshot[r.id] = r.text })
    setEditingChecklist(checklist)
    setFormStage(checklist.stage)
    setFormTitle(checklist.title)
    setOriginalTitle(checklist.title)
    setFormItems(rows)
    setOriginalItemsSnapshot(snapshot)
    setFormIsDefault(checklist.isDefault)
    setFormClientId(checklist.clientId === 'default' ? '' : checklist.clientId)
    setPreviewOpen(false)
    setDialogOpen(true)
  }

  const updateItemText = (id: string, text: string) => {
    setFormItems((prev) => prev.map((r) => (r.id === id ? { ...r, text } : r)))
  }

  const removeItem = (id: string) => {
    setFormItems((prev) => prev.filter((r) => r.id !== id))
  }

  const addItem = () => {
    const id = newItemId()
    focusItemIdRef.current = id
    setFormItems((prev) => [...prev, { id, text: '' }])
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setFormItems((prev) => {
      const oldIndex = prev.findIndex((r) => r.id === active.id)
      const newIndex = prev.findIndex((r) => r.id === over.id)
      if (oldIndex < 0 || newIndex < 0) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const isRowDirty = (row: ItemRow) => {
    const original = originalItemsSnapshot[row.id]
    if (original === undefined) return true
    return row.text !== original
  }

  const titleDirty = formTitle !== originalTitle
  const itemsDirty = (() => {
    const originalIds = Object.keys(originalItemsSnapshot)
    if (formItems.length !== originalIds.length) return true
    return formItems.some((r, idx) => isRowDirty(r) || r.id !== originalIds[idx])
  })()
  // Stage has to count as a change on its own. A checklist left on a retired
  // stage can only be repaired by re-pointing it, and without this the Save
  // button stayed disabled unless the admin also edited the title or an item.
  const stageDirty = editingChecklist ? formStage !== editingChecklist.stage : false
  const isDirty = titleDirty || itemsDirty || stageDirty || !editingChecklist
  const trimmedItemsCount = formItems.filter((r) => r.text.trim().length > 0).length
  const canSave = !saving && formTitle.trim().length > 0 && trimmedItemsCount > 0 && isDirty

  const handleSave = async () => {
    const items = formItems.map((r) => r.text.trim()).filter(Boolean)
    if (!formTitle.trim() || items.length === 0) return

    setSaving(true)
    try {
      if (editingChecklist) {
        await outreachChecklistsApi.manage({
          action: 'update',
          id: editingChecklist.id,
          stage: formStage,
          title: formTitle.trim(),
          items,
          isDefault: formIsDefault,
          clientId: formClientId || 'default',
        })
        showSuccess('Checklist updated')
      } else {
        await outreachChecklistsApi.manage({
          action: 'create',
          stage: formStage,
          title: formTitle.trim(),
          items,
          isDefault: formIsDefault,
          clientId: formClientId || 'default',
        })
        showSuccess('Checklist created')
      }
      setDialogOpen(false)
      fetchChecklists()
    } catch (err) {
      console.error('Failed to save checklist:', err)
      showError('Failed to save checklist')
    }
    setSaving(false)
  }

  const handleDelete = async () => {
    try {
      await outreachChecklistsApi.manage({ action: 'delete', id: deletingId })
      showSuccess('Checklist deleted')
      setDeleteConfirmOpen(false)
      fetchChecklists()
    } catch (err) {
      console.error('Failed to delete checklist:', err)
      showError('Failed to delete checklist')
    }
  }

  const newChecklistButton = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
      New Checklist
    </Button>
  )

  // A page-shaped skeleton rather than a centred spinner: the header and the
  // table frame hold their place, so nothing jumps when the list arrives.
  if (loading) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Skeleton variant="text" width={210} height={30} />
          <Box sx={{ flexGrow: 1 }} />
          <Skeleton variant="rounded" width={140} height={36} />
        </Stack>
        <Paper variant="outlined" sx={tableStyles.tableContainer}>
          <Table sx={tableStyles.table}>
            <TableBody>
              <TableSkeleton rows={6} columns={isPhone ? 2 : 5} />
            </TableBody>
          </Table>
        </Paper>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <PageHeader
          title="Outreach Checklists"
          help={<PageHelpButton articleId="page-outreach-checklists" />}
          actions={newChecklistButton}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {checklists.length === 0 ? (
        <Paper variant="outlined" sx={tableStyles.tableContainer}>
          <EmptyState
            icon={<ChecklistIcon />}
            title="No checklists yet"
            description="A checklist is the set of steps a coach works through for a contact at one outreach stage. Create the first one and it appears on every contact at that stage."
            action={newChecklistButton}
          />
        </Paper>
      ) : isPhone ? (
        /* Five columns will not fit a phone, and none of them is droppable: the
           stage says where it applies, the scope says who it applies to, and
           both are the reason you opened this page. The card keeps all of it. */
        <Stack spacing={1}>
          {checklists.map((cl) => (
            <Card key={cl.id}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.875rem' }}>
                      {cl.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontWeight: 400 }}>
                      {cl.items.length} {cl.items.length === 1 ? 'item' : 'items'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <IconButton
                      aria-label={`Edit ${cl.title}`}
                      onClick={() => openEditDialog(cl)}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label={`Delete ${cl.title}`}
                      onClick={() => { setDeletingId(cl.id); setDeleteConfirmOpen(true) }}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: 'wrap' }} useFlexGap>
                  <Chip label={stageLabel(cl.stage)} size="small" />
                  {cl.clientId === 'default' || cl.isDefault ? (
                    <Chip label="Default" size="small" color="primary" variant="outlined" />
                  ) : (
                    <Chip label="Client Override" size="small" color="warning" variant="outlined" />
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Paper variant="outlined" sx={tableStyles.tableContainer}>
          <TableContainer>
            <Table sx={tableStyles.table}>
              <TableHead>
                <TableRow>
                  <TableCell>Stage</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checklists.map((cl) => (
                  <TableRow key={cl.id}>
                    <TableCell>
                      <Chip label={stageLabel(cl.stage)} size="small" />
                    </TableCell>
                    <TableCell>{cl.title}</TableCell>
                    <TableCell>{cl.items.length} {cl.items.length === 1 ? 'item' : 'items'}</TableCell>
                    <TableCell>
                      {cl.clientId === 'default' || cl.isDefault ? (
                        <Chip label="Default" size="small" color="primary" variant="outlined" />
                      ) : (
                        <Chip label="Client Override" size="small" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" aria-label={`Edit ${cl.title}`} onClick={() => openEditDialog(cl)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Delete ${cl.title}`}
                        onClick={() => { setDeletingId(cl.id); setDeleteConfirmOpen(true) }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      <ResponsiveSheet open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            pr: 3,
          }}
        >
          <span>{editingChecklist ? 'Edit Checklist' : 'New Checklist'}</span>
          <Button
            size="small"
            variant="outlined"
            startIcon={previewOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={() => setPreviewOpen((v) => !v)}
          >
            {previewOpen ? 'Hide preview' : 'Preview'}
          </Button>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Stage</InputLabel>
              <Select value={formStage} label="Stage" onChange={(e) => setFormStage(e.target.value)}>
                {STAGES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Title"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g., Initial Contact Checklist"
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Checklist Items
              </Typography>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={formItems.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {formItems.length === 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 1 }}>
                        No items yet. Click "+ Add item" below to add one.
                      </Typography>
                    ) : (
                      formItems.map((item) => (
                        <SortableItemRow
                          key={item.id}
                          item={item}
                          isDirty={isRowDirty(item)}
                          onChange={(text) => updateItemText(item.id, text)}
                          onDelete={() => removeItem(item.id)}
                          inputRef={(el) => { itemInputsRef.current[item.id] = el }}
                        />
                      ))
                    )}
                  </Box>
                </SortableContext>
              </DndContext>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addItem}
                sx={{ mt: 1 }}
              >
                Add item
              </Button>
              <Collapse in={previewOpen} timeout={200} unmountOnExit>
                <ChecklistClientPreview
                  title={formTitle}
                  items={formItems.map((r) => r.text)}
                />
              </Collapse>
            </Box>
            <FormControlLabel
              control={<Switch checked={formIsDefault} onChange={(e) => setFormIsDefault(e.target.checked)} />}
              label="Default checklist (applies to all clients)"
            />
            {!formIsDefault && (
              <TextField
                label="Client ID (for client-specific override)"
                value={formClientId}
                onChange={(e) => setFormClientId(e.target.value)}
                size="small"
                fullWidth
                placeholder="Enter client ID"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!canSave}
          >
            {saving ? 'Saving...' : editingChecklist ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </ResponsiveSheet>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Checklist</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete this checklist. It cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
